(() => {
  const MIN_SCALE = 1;
  const MAX_SCALE = 3;
  const DOUBLE_TAP_SCALE = 2;
  const states = new WeakMap();

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const point = (touch) => ({
    x: touch.clientX,
    y: touch.clientY
  });

  const distance = (a, b) =>
    Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

  const midpoint = (a, b) => ({
    x: (a.clientX + b.clientX) / 2,
    y: (a.clientY + b.clientY) / 2
  });

  function getState(image) {
    if (!states.has(image)) {
      states.set(image, {
        scale: 1,
        x: 0,
        y: 0,
        mode: null,
        moved: false,
        lastTap: 0,
        lastTapPoint: null,
        lastTouchEnd: 0
      });
    }

    return states.get(image);
  }

  function getLimits(image, scale) {
    const ratio = Number(image.getAttribute('width')) / Number(image.getAttribute('height'));
    const width = Math.min(image.clientWidth, image.clientHeight * ratio);
    const height = width / ratio;
    return {
      x: Math.max(0, (width * scale - image.clientWidth) / 2),
      y: Math.max(0, (height * scale - image.clientHeight) / 2)
    };
  }

  function render(image, animate = false) {
    const state = getState(image);
    const limits = getLimits(image, state.scale);

    state.x = clamp(state.x, -limits.x, limits.x);
    state.y = clamp(state.y, -limits.y, limits.y);

    image.style.transition = animate ? 'transform 180ms ease' : 'none';
    image.style.transform =
      `translate(${state.x}px, ${state.y}px) scale(${state.scale})`;

    const zoomed = state.scale > 1.01;
    image.classList.toggle('mg-is-zoomed', zoomed);

    const modal = image.closest('product-modal');
    if (modal && image.classList.contains('active')) modal.classList.toggle('mg-zoom-active', zoomed);

  }

  function reset(image) {
    const state = getState(image);
    Object.assign(state, { scale: 1, x: 0, y: 0, mode: null, mouse: null, lastTap: 0, lastTapPoint: null });
    render(image, true);
  }

  function zoomAtPoint(image, tapPoint) {
    const state = getState(image);

    if (state.scale > 1.01) {
      reset(image);
      return;
    }

    state.scale = DOUBLE_TAP_SCALE;
    state.x = (window.innerWidth / 2 - tapPoint.x) * (state.scale - 1);
    state.y = (window.innerHeight / 2 - tapPoint.y) * (state.scale - 1);

    render(image, true);
  }

  function initialiseImage(image) {
    if (image.classList.contains('mg-pinch-image')) return;

    image.classList.add('mg-pinch-image');
    const state = getState(image);

    image.addEventListener(
      'touchstart',
      (event) => {
        state.moved = false;
        state.startedAt = performance.now();

        if (event.touches.length === 2) {
          event.preventDefault();

          state.mode = 'pinch';
          state.lastTap = 0;
          state.lastTapPoint = null;
          state.startDistance = Math.max(1, distance(event.touches[0], event.touches[1]));
          state.startScale = state.scale;
          state.startMidpoint = midpoint(event.touches[0], event.touches[1]);
          state.startX = state.x;
          state.startY = state.y;
        } else if (event.touches.length === 1) {
          state.mode = state.scale > 1.01 ? 'pan' : 'tap';
          state.startPoint = point(event.touches[0]);
          state.startX = state.x;
          state.startY = state.y;

          if (state.mode === 'pan') event.preventDefault();
        }
      },
      { passive: false }
    );

    image.addEventListener(
      'touchmove',
      (event) => {
        if (event.touches.length === 2 && state.mode === 'pinch') {
          event.preventDefault();

          const currentDistance = distance(event.touches[0], event.touches[1]);
          const currentMidpoint = midpoint(event.touches[0], event.touches[1]);

          const newScale = clamp(
            state.startScale * (currentDistance / state.startDistance),
            MIN_SCALE,
            MAX_SCALE
          );

          const ratio = newScale / state.startScale;

          state.x =
            currentMidpoint.x -
            window.innerWidth / 2 -
            ratio *
              (state.startMidpoint.x -
                window.innerWidth / 2 -
                state.startX);

          state.y =
            currentMidpoint.y -
            window.innerHeight / 2 -
            ratio *
              (state.startMidpoint.y -
                window.innerHeight / 2 -
                state.startY);

          state.scale = newScale;
          state.moved = true;

          render(image);
          return;
        }

        if (event.touches.length === 1 && state.startPoint) {
          const currentPoint = point(event.touches[0]);
          const movement = Math.hypot(
            currentPoint.x - state.startPoint.x,
            currentPoint.y - state.startPoint.y
          );

          if (movement > 6) state.moved = true;

          if (state.scale > 1.01) {
            event.preventDefault();

            state.x =
              state.startX + currentPoint.x - state.startPoint.x;
            state.y =
              state.startY + currentPoint.y - state.startPoint.y;

            render(image);
          }
        }
      },
      { passive: false }
    );

    image.addEventListener(
      'touchend',
      (event) => {
        if (event.touches.length) {
          if (event.touches.length === 1 && state.mode === 'pinch') {
            state.mode = 'pan';
            state.moved = true;
            state.startPoint = point(event.touches[0]);
            state.startX = state.x;
            state.startY = state.y;
          }
          return;
        }

        const endedAt = state.lastTouchEnd = performance.now();
        const changedTouch = event.changedTouches[0];
        const tapPoint = changedTouch ? point(changedTouch) : null;

        const wasTap =
          !state.moved &&
          state.mode !== 'pinch' &&
          endedAt - state.startedAt < 280 &&
          tapPoint;

        if (wasTap) {
          event.preventDefault();
          const closeToLastTap =
            state.lastTapPoint &&
            Math.hypot(
              tapPoint.x - state.lastTapPoint.x,
              tapPoint.y - state.lastTapPoint.y
            ) < 40;

          if (endedAt - state.lastTap < 320 && closeToLastTap) {
            event.preventDefault();
            zoomAtPoint(image, tapPoint);

            state.lastTap = 0;
            state.lastTapPoint = null;
          } else {
            state.lastTap = endedAt;
            state.lastTapPoint = tapPoint;
          }
        }

        if (state.scale < 1.02 && state.mode !== 'tap') reset(image);
        state.mode = null;
      },
      { passive: false }
    );

    image.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || state.scale <= 1.01) return;
      event.preventDefault();
      state.mouse = { x: event.clientX, y: event.clientY, startX: state.x, startY: state.y };
      image.setPointerCapture(event.pointerId);
    });
    image.addEventListener('pointermove', (event) => {
      if (!state.mouse) return;
      state.x = state.mouse.startX + event.clientX - state.mouse.x;
      state.y = state.mouse.startY + event.clientY - state.mouse.y;
      render(image);
    });
    image.addEventListener('pointerup', () => { state.mouse = null; });
    image.addEventListener('pointercancel', () => { state.mouse = null; });
    image.addEventListener('touchcancel', () => { state.mode = null; state.lastTap = 0; });

    image.addEventListener('dblclick', (event) => {
      event.preventDefault();
      if (state.lastTouchEnd && performance.now() - state.lastTouchEnd < 500) return;
      zoomAtPoint(image, {
        x: event.clientX,
        y: event.clientY
      });
    });
  }

  function initialiseModals() {
    document
      .querySelectorAll('product-modal.product-media-modal')
      .forEach((modal) => {
        modal
          .querySelectorAll('.product-media-modal__content > img')
          .forEach(initialiseImage);

        if (modal.dataset.mgZoomReady) return;
        modal.dataset.mgZoomReady = 'true';

        modal.addEventListener('mg:media-change', () => {
          modal.querySelectorAll('img.mg-pinch-image.mg-is-zoomed').forEach((image) => reset(image));
        });
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseModals);
  } else {
    initialiseModals();
  }

  document.addEventListener('shopify:section:load', initialiseModals);
})();
